import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin, Wrench, MessageSquare, DollarSign, CheckCircle, Zap, Clock, MapPinned, Shield, AlertTriangle } from "lucide-react";
import Footer from "@/components/Footer";

// Logos
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function ContractorWelcomeLetterPage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/40 rounded-full">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-red-400 text-sm font-medium">Critical Response Network</span>
          </div>
        </div>
      </header>

      {/* Letter Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
          
          {/* Letter Header - Urgent Theme */}
          <div className="bg-gradient-to-r from-red-600 to-amber-600 px-8 py-10 text-center">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-20 h-20 mx-auto mb-4 rounded-xl" />
            <h1 className="text-3xl font-bold text-white mb-2">Welcome to the Critical Response Network</h1>
            <p className="text-red-100">You&apos;re Not Bidding. You&apos;re Responding to Emergencies.</p>
          </div>
          
          {/* Letter Body */}
          <div className="px-8 py-10 lg:px-12 text-slate-300">
            <p className="text-slate-500 text-sm mb-8">January 2026</p>
            
            <p className="text-white text-lg leading-relaxed mb-6">
              Dear Contractor Partner,
            </p>
            
            <p className="leading-relaxed mb-6">
              Welcome to <strong className="text-white">AskIsaac</strong> — the platform that connects qualified contractors 
              directly to healthcare facilities when they need you most.
            </p>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-bold text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                This Is NOT a Bidding Platform
              </h2>
              <p className="text-slate-300">
                This isn&apos;t a planned-procurement portal. When a hospital&apos;s OR is flooded, their sterilizers are down, 
                or their HVAC fails — they can&apos;t wait for a 2-week bidding process. They need someone 
                <strong className="text-white"> qualified</strong> and <strong className="text-white">available</strong> NOW.
              </p>
            </div>
            
            <h2 className="text-xl font-semibold text-white mt-8 mb-4">How It Works</h2>
            
            <p className="leading-relaxed mb-6">
              When a hospital posts an urgent need, AskIsaac sends an alert to every qualified contractor in the network. 
              <strong className="text-amber-400"> The first qualified responder wins the job</strong>. No bidding wars. 
              No waiting. Just fast response and fair pay.
            </p>

            {/* Response Tiers */}
            <div className="bg-slate-700/50 rounded-xl p-6 mb-8 border border-slate-600">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Response Tiers — Faster Response = Premium Pay
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-800 rounded-lg border border-slate-600">
                  <p className="font-bold text-slate-300">Standard</p>
                  <p className="text-lg font-semibold text-white">24-48 hrs</p>
                  <p className="text-xs text-slate-500">Base rate</p>
                </div>
                <div className="text-center p-3 bg-amber-900/30 rounded-lg border border-amber-500/40">
                  <p className="font-bold text-amber-400">Priority</p>
                  <p className="text-lg font-semibold text-white">4-8 hrs</p>
                  <p className="text-xs text-amber-400">Premium rate</p>
                </div>
                <div className="text-center p-3 bg-red-900/30 rounded-lg border-2 border-red-500/50">
                  <p className="font-bold text-red-400">STAT</p>
                  <p className="text-lg font-semibold text-white">2 hrs</p>
                  <p className="text-xs text-red-400 font-medium">Emergency premium</p>
                </div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-white mt-8 mb-4">Your Contractor Portal</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600">
                <Zap className="w-8 h-8 text-red-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Instant Job Alerts</h3>
                <p className="text-slate-400 text-sm">Get notified the moment a hospital needs your skills. First to respond wins.</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600">
                <MessageSquare className="w-8 h-8 text-cyan-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Telegram Notifications</h3>
                <p className="text-slate-400 text-sm">Real-time job alerts and dispatch communication — even from the field.</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600">
                <MapPinned className="w-8 h-8 text-emerald-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">GPS Check-In/Out</h3>
                <p className="text-slate-400 text-sm">One-tap arrival verification. Hospital and admin see you&apos;re on-site in real-time.</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-5 border border-slate-600">
                <DollarSign className="w-8 h-8 text-amber-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Fast Payment</h3>
                <p className="text-slate-400 text-sm">Submit invoices directly through the portal. No chasing payments.</p>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-white mt-8 mb-4">The Process</h2>
            
            <ol className="list-decimal list-inside space-y-3 mb-8 text-slate-300">
              <li><strong className="text-white">Get Notified</strong> — Job alerts hit your phone instantly when a hospital posts a need.</li>
              <li><strong className="text-white">Accept Fast</strong> — First qualified contractor to respond gets the job. No bidding.</li>
              <li><strong className="text-white">GPS Check-In</strong> — Tap to check in when you arrive. Everyone knows you&apos;re there.</li>
              <li><strong className="text-white">Complete & Document</strong> — Log your work, upload photos, capture signature.</li>
              <li><strong className="text-white">GPS Check-Out</strong> — Tap to check out. Time on-site is recorded automatically.</li>
              <li><strong className="text-white">Get Paid</strong> — Submit your invoice through the portal. Fast processing.</li>
            </ol>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-amber-400 mb-3">Why Hospitals Choose Contractors Like You</h3>
              <p className="text-slate-300">
                Healthcare facilities can&apos;t afford downtime. When their equipment fails, patients wait. When sterilizers break, 
                surgeries get postponed. They need professionals who can respond quickly and get the job done right. 
                That&apos;s why they trust the AskIsaac network — and contractors like you.
              </p>
            </div>
            
            <p className="leading-relaxed mb-8">
              Your contractor portal is ready at <a href="https://sterilemate.com/contractor/login" className="text-amber-400 font-semibold hover:underline">AskIsaac.io</a>. 
              Log in with the credentials sent to your email, complete your profile, and start receiving job notifications.
            </p>
            
            {/* Signature */}
            <div className="border-t border-slate-700 pt-8 mt-8">
              <p className="text-white font-semibold mb-1">Welcome to the network,</p>
              <p className="text-xl font-bold text-white mb-1">George Broughton III</p>
              <p className="text-slate-400 mb-6">President & CEO, Sterile Mate LLC</p>
              
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-12 h-12 rounded-lg" />
                  <div>
                    <p className="font-bold text-amber-400">ASKISAAC</p>
                    <p className="text-slate-500 text-sm"></p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-amber-400" />
                    <span>800.988.3892</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-400" />
                    <span>info@sterilemate.com</span>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    <span>396 W Greens Road, Suite 1139, Houston, TX 77067</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA */}
          <div className="bg-gradient-to-r from-red-600 to-amber-600 px-8 py-8 text-center">
            <p className="text-white text-lg mb-4">Ready to start responding to hospital emergencies?</p>
            <Link
              to="/contractor/login"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              Access Contractor Portal
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
